<ion-header [translucent]="true">
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-button (click)="goBack()">
        <ion-icon name="arrow-back" slot="icon-only"></ion-icon>
      </ion-button>
    </ion-buttons>
    <ion-title>Article Taxonomy Analysis</ion-title>
    <ion-buttons slot="end">
      <ion-button (click)="onRefresh()" [disabled]="isLoading">
        <ion-icon name="refresh-outline" slot="icon-only"></ion-icon>
      </ion-button>
    </ion-buttons>
  </ion-toolbar>
</ion-header>

<ion-content [fullscreen]="true">
  <ion-grid style="margin: 3px">
    <!-- Error State -->
    <ion-row *ngIf="error">
      <ion-col>
        <ion-item color="danger">
          <ion-icon name="warning-outline" slot="start"></ion-icon>
          {{ error }}
        </ion-item>
      </ion-col>
    </ion-row>

    <!-- Loading State -->
    <ion-row
      *ngIf="isLoading"
      class="ion-justify-content-center"
      style="padding: 3rem"
    >
      <ion-col size="auto" class="ion-text-center">
        <ion-spinner color="primary"></ion-spinner>
        <p style="margin-top: 1rem">Loading article analysis...</p>
      </ion-col>
    </ion-row>

    <!-- Article Analysis Content -->
    <div *ngIf="!isLoading && !error && analysisData">
      <!-- Article Header -->
      <ion-row>
        <ion-col>
          <ion-card>
            <ion-card-header>
              <ion-card-title>{{ analysisData.article.title }}</ion-card-title>
              <ion-card-subtitle>
                Article ID: {{ analysisData.article.id }} • Updated:
                {{ analysisData.article.updatedAt | date: "medium" }}
                <span *ngIf="analysisData.article.projectId">
                  • Project: {{ analysisData.article.projectId }}</span
                >
              </ion-card-subtitle>
            </ion-card-header>
            <ion-card-content>
              <div
                style="
                  display: flex;
                  gap: 1rem;
                  align-items: center;
                  margin-bottom: 1rem;
                  flex-wrap: wrap;
                "
              >
                <a [href]="getArticleUrl()" target="_blank">
                  <ion-button fill="outline" size="small">
                    <ion-icon name="open-outline" slot="start"></ion-icon>
                    Edit Article
                  </ion-button>
                </a>
                <ion-chip color="medium">
                  {{ analysisData.classifiedChunks }} /
                  {{ analysisData.totalChunks }} chunks classified
                </ion-chip>
                <ion-chip
                  [color]="getStrategyColor(analysisData.strategyInfo.strategy)"
                  size="small"
                >
                  {{ getStrategyLabel(analysisData.strategyInfo.strategy) }}
                </ion-chip>
                <ion-chip
                  *ngIf="!analysisData.strategyInfo.usesEmbeddings"
                  color="tertiary"
                  size="small"
                >
                  No Embeddings Required
                </ion-chip>
              </div>

              <div style="margin-bottom: 1rem">
                <div
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.5rem;
                  "
                >
                  <span style="font-weight: 500">Classification Progress</span>
                  <span style="font-weight: 600"
                    >{{ getClassificationProgress() | number: "1.1-1" }}%</span
                  >
                </div>
                <ion-progress-bar
                  [value]="getClassificationProgress() / 100"
                  [color]="
                    getClassificationProgress() >= 90
                      ? 'success'
                      : getClassificationProgress() >= 50
                        ? 'warning'
                        : 'danger'
                  "
                >
                </ion-progress-bar>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- Aggregated Results -->
      <ion-row *ngIf="analysisData.aggregatedResult">
        <ion-col>
          <ion-card>
            <ion-card-header>
              <ion-card-title>Aggregated Classification</ion-card-title>
              <ion-card-subtitle>
                Final article-level taxonomy decision using
                {{ getStrategyLabel(analysisData.strategyInfo.strategy) }}
                <span
                  *ngIf="analysisData.strategyInfo.chunkType === 'container'"
                >
                  ({{ analysisData.strategyInfo.chunkType }} chunks)</span
                >
                <span *ngIf="analysisData.strategyInfo.chunkType === 'content'">
                  ({{ analysisData.strategyInfo.chunkType }} chunks)</span
                >
              </ion-card-subtitle>
            </ion-card-header>
            <ion-card-content>
              <div
                style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 1.5rem;
                  padding: 1rem;
                  background: var(--ion-color-light);
                  border-radius: 8px;
                "
              >
                <div>
                  <h3 style="margin: 0; font-size: 1.2rem; font-weight: 600">
                    {{ analysisData.aggregatedResult.primaryCategory }}
                  </h3>
                  <p
                    style="
                      margin: 0.25rem 0 0 0;
                      font-size: 0.9rem;
                      opacity: 0.8;
                    "
                  >
                    Primary Category
                  </p>
                </div>
                <ion-chip
                  [color]="
                    getConfidenceColor(
                      analysisData.aggregatedResult.primaryConfidence
                    )
                  "
                >
                  {{
                    analysisData.aggregatedResult.primaryConfidence * 100
                      | number: "1.1-1"
                  }}% •
                  {{
                    getConfidenceText(
                      analysisData.aggregatedResult.primaryConfidence
                    )
                  }}
                </ion-chip>
              </div>

              <div
                *ngIf="analysisData.aggregatedResult.allCategories.length > 1"
              >
                <h4 style="margin: 1rem 0 0.5rem 0">All Category Weights</h4>
                <div
                  *ngFor="
                    let cat of analysisData.aggregatedResult.allCategories;
                    trackBy: trackByCategory
                  "
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.5rem 0;
                    border-bottom: 1px solid var(--ion-color-light-shade);
                  "
                >
                  <div>
                    <span style="font-weight: 500">{{ cat.category }}</span>
                    <button
                      (click)="viewCategoryAnalysis(cat.category)"
                      style="
                        background: none;
                        border: none;
                        color: var(--ion-color-primary);
                        margin-left: 0.5rem;
                        text-decoration: underline;
                        cursor: pointer;
                        font-size: 0.8rem;
                      "
                    >
                      analyze
                    </button>
                  </div>
                  <div style="text-align: right">
                    <div style="font-size: 0.9rem; font-weight: 600">
                      Weight: {{ cat.weight * 100 | number: "1.1-1" }}%
                    </div>
                    <div style="font-size: 0.8rem; opacity: 0.7">
                      {{ cat.chunkCount }} chunks • Avg:
                      {{ cat.avgConfidence * 100 | number: "1.1-1" }}%
                    </div>
                  </div>
                </div>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- No Aggregated Results -->
      <ion-row *ngIf="!analysisData.aggregatedResult">
        <ion-col>
          <ion-card>
            <ion-card-content style="text-align: center; padding: 2rem">
              <ion-icon
                name="alert-circle-outline"
                style="font-size: 2rem; opacity: 0.5; margin-bottom: 1rem"
              ></ion-icon>
              <p style="margin: 0">
                No aggregated results available. This article may not have
                sufficient chunk classifications.
              </p>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- Chunk Classifications -->
      <ion-row>
        <ion-col>
          <ion-card>
            <ion-card-header>
              <ion-card-title>High Confidence Classifications</ion-card-title>
              <ion-card-subtitle
                >Chunks above
                {{
                  analysisData.strategyInfo.confidenceThresholds.aggregation *
                    100 | number: "1.1-1"
                }}% threshold ({{
                  analysisData.chunkClassifications.length
                }}
                chunks)</ion-card-subtitle
              >
            </ion-card-header>
            <ion-card-content>
              <div
                *ngIf="analysisData.chunkClassifications.length === 0"
                style="text-align: center; padding: 2rem"
              >
                <ion-icon
                  name="documents-outline"
                  style="font-size: 2rem; opacity: 0.5; margin-bottom: 1rem"
                ></ion-icon>
                <p style="margin: 0">
                  No chunk classifications found for this article.
                </p>
              </div>

              <div *ngIf="analysisData.chunkClassifications.length > 0">
                <div
                  *ngFor="
                    let chunk of analysisData.chunkClassifications;
                    trackBy: trackByChunk
                  "
                  style="
                    margin-bottom: 1.5rem;
                    border: 1px solid var(--ion-color-light);
                    border-radius: 8px;
                    padding: 1rem;
                  "
                >
                  <!-- Chunk Header -->
                  <div
                    style="
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      margin-bottom: 1rem;
                    "
                  >
                    <div
                      style="display: flex; align-items: center; gap: 0.5rem"
                    >
                      <ion-chip color="medium" size="small"
                        >#{{ chunk.chunkPosition }}</ion-chip
                      >
                      <span
                        style="
                          font-weight: 600;
                          color: var(--ion-color-primary);
                        "
                        >{{ chunk.taxonomyCategory }}</span
                      >
                      <ion-chip
                        [color]="getMethodColor(chunk.method)"
                        size="small"
                        >{{ chunk.method }}</ion-chip
                      >
                    </div>
                    <ion-chip [color]="getConfidenceColor(chunk.confidence)">
                      {{ chunk.confidence * 100 | number: "1.1-1" }}%
                    </ion-chip>
                  </div>

                  <!-- Chunk Content -->
                  <div
                    style="
                      background: var(--ion-color-light);
                      padding: 1rem;
                      border-radius: 4px;
                      margin-bottom: 1rem;
                    "
                  >
                    <p
                      style="
                        margin: 0;
                        line-height: 1.5;
                        font-family: monospace;
                        font-size: 0.9rem;
                      "
                    >
                      {{ truncateContent(chunk.chunkContent) }}
                    </p>
                    <button
                      *ngIf="chunk.chunkContent.length > 200"
                      (click)="showFullContent(chunk.chunkContent)"
                      style="
                        background: none;
                        border: none;
                        color: var(--ion-color-primary);
                        margin-top: 0.5rem;
                        text-decoration: underline;
                        cursor: pointer;
                        font-size: 0.8rem;
                      "
                    >
                      Show full content
                    </button>
                  </div>

                  <!-- Chunk Metadata -->
                  <div
                    style="
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      font-size: 0.8rem;
                      opacity: 0.7;
                      gap: 1rem;
                    "
                  >
                    <span
                      >Classified:
                      {{ chunk.classifiedAt | date: "short" }}</span
                    >
                    <span style="font-family: monospace; word-break: break-all"
                      >Fingerprint: {{ chunk.contentFingerprint }}</span
                    >
                  </div>
                </div>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- Low Confidence Samples -->
      <ion-row *ngIf="analysisData.lowConfidenceChunks.length > 0">
        <ion-col>
          <ion-card>
            <ion-card-header>
              <ion-card-title style="color: var(--ion-color-warning)"
                >Low Confidence Samples</ion-card-title
              >
              <ion-card-subtitle
                >Chunks below
                {{
                  analysisData.strategyInfo.confidenceThresholds.aggregation *
                    100 | number: "1.1-1"
                }}% threshold - these may need review ({{
                  analysisData.lowConfidenceChunks.length
                }}
                chunks)</ion-card-subtitle
              >
            </ion-card-header>
            <ion-card-content>
              <div
                style="
                  margin-bottom: 1rem;
                  padding: 1rem;
                  background: var(--ion-color-warning-shade);
                  color: var(--ion-color-warning-contrast);
                  border-radius: 8px;
                "
              >
                <ion-icon
                  name="warning"
                  style="margin-right: 0.5rem"
                ></ion-icon>
                <strong>Review Needed:</strong> These chunks have low confidence
                scores and may be incorrectly classified. Consider updating the
                strategy or manually reviewing these classifications.
              </div>

              <div
                *ngFor="
                  let chunk of analysisData.lowConfidenceChunks;
                  trackBy: trackByChunk
                "
                style="
                  margin-bottom: 1.5rem;
                  border: 1px solid var(--ion-color-warning);
                  border-radius: 8px;
                  padding: 1rem;
                "
              >
                <!-- Chunk Header -->
                <div
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;
                  "
                >
                  <div style="display: flex; align-items: center; gap: 0.5rem">
                    <ion-chip color="medium" size="small"
                      >#{{ chunk.chunkPosition }}</ion-chip
                    >
                    <span
                      style="font-weight: 600; color: var(--ion-color-primary)"
                      >{{ chunk.taxonomyCategory }}</span
                    >
                    <ion-chip
                      [color]="getMethodColor(chunk.method)"
                      size="small"
                      >{{ chunk.method }}</ion-chip
                    >
                  </div>
                  <ion-chip [color]="getConfidenceColor(chunk.confidence)">
                    {{ chunk.confidence * 100 | number: "1.1-1" }}% •
                    {{ getConfidenceText(chunk.confidence) }}
                  </ion-chip>
                </div>

                <!-- Chunk Content -->
                <div
                  style="
                    background: var(--ion-color-light);
                    padding: 1rem;
                    border-radius: 4px;
                    margin-bottom: 1rem;
                  "
                >
                  <p
                    style="
                      margin: 0;
                      line-height: 1.5;
                      font-family: monospace;
                      font-size: 0.9rem;
                    "
                  >
                    {{ truncateContent(chunk.chunkContent) }}
                  </p>
                  <button
                    *ngIf="chunk.chunkContent.length > 200"
                    (click)="showFullContent(chunk.chunkContent)"
                    style="
                      background: none;
                      border: none;
                      color: var(--ion-color-primary);
                      margin-top: 0.5rem;
                      text-decoration: underline;
                      cursor: pointer;
                      font-size: 0.8rem;
                    "
                  >
                    Show full content
                  </button>
                </div>

                <!-- Chunk Metadata -->
                <div
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 0.8rem;
                    opacity: 0.7;
                    gap: 1rem;
                  "
                >
                  <span
                    >Classified: {{ chunk.classifiedAt | date: "short" }}</span
                  >
                  <span style="font-family: monospace; word-break: break-all"
                    >Fingerprint: {{ chunk.contentFingerprint }}</span
                  >
                </div>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>
    </div>
  </ion-grid>
</ion-content>
